import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Snackbar,
  Chip,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { adminService } from '../../services/adminService';
import { UserListItem } from '../../types/user';
import { useUser } from '../../contexts/AuthContext';
import ApprovalCard from '../../components/admin/ApprovalCard';

/**
 * ApprovalQueue Component
 * Page for reviewing and approving pending user registrations
 *
 * Features:
 * - Display all pending user registrations
 * - Quick approve/reject actions
 * - Bulk approve/reject
 * - Rejection with reason
 * - Real-time updates
 * - Email notifications (when configured)
 */
const ApprovalQueue: React.FC = () => {
  const navigate = useNavigate();
  const currentUser = useUser();

  // State
  const [pendingUsers, setPendingUsers] = useState<UserListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingUserId, setProcessingUserId] = useState<string | null>(null);
  const [rejectDialog, setRejectDialog] = useState<{
    open: boolean;
    userId: string;
    userName: string;
  } | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'info' });

  /**
   * Load pending users on mount
   */
  useEffect(() => {
    loadPendingUsers();
  }, []);

  /**
   * Load all pending users
   */
  const loadPendingUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const users = await adminService.getPendingApprovals();
      setPendingUsers(users);
    } catch (err: any) {
      console.error('Error loading pending users:', err);
      setError(err.message || 'Failed to load pending users');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle approve user
   */
  const handleApprove = async (userId: string) => {
    if (!currentUser?.uid) return;

    try {
      setProcessingUserId(userId);
      await adminService.approveUser(userId, currentUser.uid);

      showSnackbar('User approved successfully', 'success');

      // Remove from pending list
      setPendingUsers((prev) => prev.filter((user) => user.uid !== userId));
    } catch (err: any) {
      console.error('Error approving user:', err);
      showSnackbar(err.message || 'Failed to approve user', 'error');
    } finally {
      setProcessingUserId(null);
    }
  };

  /**
   * Handle reject user - open dialog
   */
  const handleRejectClick = (userId: string) => {
    const user = pendingUsers.find((u) => u.uid === userId);
    if (!user) return;

    setRejectDialog({
      open: true,
      userId: userId,
      userName: user.displayName,
    });
    setRejectReason('');
  };

  /**
   * Handle reject user - confirm
   */
  const handleRejectConfirm = async () => {
    if (!rejectDialog || !currentUser?.uid) return;

    try {
      setProcessingUserId(rejectDialog.userId);
      await adminService.rejectUser(
        rejectDialog.userId,
        currentUser.uid,
        rejectReason || 'Registration rejected by admin'
      );

      showSnackbar('User rejected successfully', 'success');

      // Remove from pending list
      setPendingUsers((prev) => prev.filter((user) => user.uid !== rejectDialog.userId));

      setRejectDialog(null);
      setRejectReason('');
    } catch (err: any) {
      console.error('Error rejecting user:', err);
      showSnackbar(err.message || 'Failed to reject user', 'error');
    } finally {
      setProcessingUserId(null);
    }
  };

  /**
   * Handle bulk approve all
   */
  const handleBulkApprove = async () => {
    if (!currentUser?.uid || pendingUsers.length === 0) return;

    const userIds = pendingUsers.map((u) => u.uid);

    try {
      setLoading(true);

      const result = await adminService.performBulkAction(
        {
          action: 'approve',
          userIds,
          reason: 'Bulk approval by admin',
        },
        currentUser.uid,
        currentUser.displayName || 'Unknown Admin'
      );

      if (result.successful > 0) {
        showSnackbar(
          `${result.successful} users approved successfully`,
          'success'
        );
      }

      if (result.failed > 0) {
        showSnackbar(
          `${result.failed} users failed to approve`,
          'error'
        );
      }

      // Reload pending users
      await loadPendingUsers();
    } catch (err: any) {
      console.error('Error bulk approving users:', err);
      showSnackbar(err.message || 'Failed to bulk approve users', 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Show snackbar message
   */
  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  /**
   * Close snackbar
   */
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  /**
   * Close reject dialog
   */
  const handleCloseRejectDialog = () => {
    setRejectDialog(null);
    setRejectReason('');
  };

  /**
   * Render loading state
   */
  if (loading && pendingUsers.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default', py: 4 }}>
      <Container maxWidth="xl">
        {/* Back Button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin')}
          sx={{ mb: 3 }}
        >
          Back to Dashboard
        </Button>

        {/* Page Header */}
        <Box mb={4}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="h4" component="h1" fontWeight={700}>
                User Approval Queue
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Review and approve pending user registrations
              </Typography>
            </Box>

            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadPendingUsers}
                disabled={loading}
              >
                Refresh
              </Button>
              {pendingUsers.length > 0 && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircleIcon />}
                  onClick={handleBulkApprove}
                  disabled={loading}
                >
                  Approve All ({pendingUsers.length})
                </Button>
              )}
            </Box>
          </Box>

          {/* Statistics */}
          <Paper sx={{ p: 2, bgcolor: 'primary.light' }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Chip
                label={`${pendingUsers.length} Pending`}
                color="warning"
                sx={{ fontWeight: 600 }}
              />
              <Divider orientation="vertical" flexItem />
              <Typography variant="body2" color="text.secondary">
                {pendingUsers.filter((u) => u.isEmailVerified).length} with verified email
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {pendingUsers.filter((u) => !u.isEmailVerified).length} unverified
              </Typography>
            </Box>
          </Paper>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Empty State */}
        {pendingUsers.length === 0 && !loading ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.light', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No Pending Approvals
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              All user registrations have been reviewed. Check back later for new registrations.
            </Typography>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/admin')}
            >
              Back to Dashboard
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {pendingUsers.map((user) => (
              <Grid item xs={12} sm={6} md={4} key={user.uid}>
                <ApprovalCard
                  user={user}
                  onApprove={handleApprove}
                  onReject={handleRejectClick}
                  loading={processingUserId === user.uid}
                />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Reject Dialog */}
        <Dialog
          open={rejectDialog?.open || false}
          onClose={handleCloseRejectDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Reject User Registration</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Are you sure you want to reject the registration for{' '}
              <strong>{rejectDialog?.userName}</strong>?
            </DialogContentText>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Rejection Reason (Optional)"
              placeholder="Provide a reason for rejection..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              helperText="This reason will be logged in the audit trail"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseRejectDialog}>Cancel</Button>
            <Button
              onClick={handleRejectConfirm}
              color="error"
              variant="contained"
              startIcon={<CancelIcon />}
              disabled={processingUserId !== null}
            >
              Reject User
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default ApprovalQueue;
